/* global TextEncoder, crypto, Excel */
/**
 * Market Watch Pipeline — shared execution logic for both the React UI and ribbon commands.
 *
 * Extracted from MarketWatchRoute.tsx so that ribbon Fetch/Refresh buttons
 * can run the same fetch → analyze → poll → render pipeline without React state.
 */

import {
  fetchMarketWatchData,
  startMarketWatchAnalysis,
  getIdToken,
  MarketWatchAnalyzeResult,
} from "./apiService";
import { config } from "./config";
import {
  CUSTOM_PROPERTIES,
  SHEET_PREFIXES,
  JOB_STATUS,
  PRICE_CLUSTER_MODES,
  MW_SINGLE_RECOMMENDATIONS_VIEW,
} from "./constants";
import {
  saveMarketWatchFilters,
  tagMWRunSheets,
  MarketWatchFilterState,
} from "./marketWatchFilterState";
import {
  MARKET_WATCH_SHEET,
  FilterContext,
  createDataSheet,
  createPivotSheet,
  createPivotSourceSheet,
  createFlattenedGridSheet,
  createAnalysisSheet,
  renderExcelStyledReport,
  sanitizeErrorReport,
  generateTimestamp,
  buildFeatureFirstRowOrder,
} from "../taskpane/components/Market-Watch";
import type { ExcelStyledReport } from "../taskpane/components/Market-Watch";

// ─── Public types ────────────────────────────────────────────────

export interface PipelineProgress {
  percent: number;
  message: string;
  phase: string;
}

export interface PipelineAbortSignal {
  cancelled: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────

/**
 * Extract the trailing timestamp from a Market Watch sheet name.
 * e.g. "MW Raw Data 10-30-45 am"   → "10-30-45 am"
 *      "MW Rec Details 02-15-00 pm" → "02-15-00 pm"
 */
export function extractMWTimestamp(sheetName: string): string | null {
  const match = sheetName.match(/(\d{2}-\d{2}-\d{2}\s+[ap]m)$/);
  return match ? match[1] : null;
}

/** Custom property key for the SHA-256 data fingerprint stored on MW Raw Data sheets. */
export const MW_DATA_FINGERPRINT_PROP = "mw_data_fingerprint";

/** CustomEvent name dispatched by ribbon refresh when data has changed. */
export const MW_STALE_ANALYSIS_EVENT = "mw-stale-analysis-detected";

/** OfficeRuntime.storage key for the stale-analysis context payload. */
export const MW_STALE_ANALYSIS_STORAGE_KEY = "mw_refresh_stale_analysis";

/** Build the "Applied Filters: Brand: … | ASIN: … | Keyword: …" summary string. */
export function buildMWFilterSummary(filters: MarketWatchFilterState): string {
  const parts: string[] = [];
  if (filters.brand.length > 0) parts.push(`Brand: ${filters.brand.join(", ")}`);
  if (filters.asin.length > 0) parts.push(`ASIN: ${filters.asin.join(", ")}`);
  if (filters.keyword.length > 0) parts.push(`Keyword: ${filters.keyword.join(", ")}`);
  return parts.length > 0 ? `Applied Filters: ${parts.join(" | ")}` : "";
}

/**
 * Compute a SHA-256 fingerprint of Market Watch data.
 * Hashes headers + row count + every cell value so that any change is detected.
 * Uses the Web Crypto API (zero dependencies, available in Office Add-in runtime).
 */
export async function computeDataFingerprint(headers: string[], rows: any[][]): Promise<string> {
  const parts: string[] = [headers.join("|"), String(rows.length)];
  for (const row of rows) {
    parts.push(row.map((cell) => String(cell ?? "")).join("|"));
  }
  const payload = parts.join("\n");
  const encoded = new TextEncoder().encode(payload);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Refresh only the MW Raw Data sheet (no analysis re-run).
 * Reads the old fingerprint, deletes/recreates the data sheet, and checks
 * whether analysis sheets exist — all in a single Excel.run for efficiency.
 */
export async function refreshMarketWatchDataOnly(
  filters: MarketWatchFilterState,
  existingTimestamp: string
): Promise<{ timestamp: string; dataChanged: boolean; analysisExists: boolean }> {
  // 1. Fetch fresh data (with exclusion filters applied at SQL level)
  const data = await fetchMarketWatchData(filters.asin, filters.keyword, filters.brand, {
    isExcludeSameBrand: filters.isExcludeSameBrand,
    excludedBrands: filters.excludedBrands,
    isExcludeSpreetailAsin: filters.isExcludeSpreetailAsin,
  });

  // 2. Compute new fingerprint
  const newFingerprint = await computeDataFingerprint(data.headers, data.rows);

  // 3. Build filter summary
  const appliedSummary = buildMWFilterSummary(filters);

  // 4. Single Excel.run: read old fingerprint → delete → create → check analysis
  const timestamp = existingTimestamp || generateTimestamp();
  let dataChanged = true; // default true if no prior fingerprint
  let analysisExists = false;

  await Excel.run(async (context) => {
    // Read old fingerprint and delete old data sheet
    try {
      const oldDataSheet = context.workbook.worksheets.getItem(`${SHEET_PREFIXES.MW_RAW_DATA} ${existingTimestamp}`);
      const prop = oldDataSheet.customProperties.getItemOrNullObject(MW_DATA_FINGERPRINT_PROP);
      prop.load("isNullObject,value"); // eslint-disable-line office-addins/no-navigational-load
      await context.sync();
      if (!prop.isNullObject) {
        dataChanged = prop.value !== newFingerprint;
      }
      oldDataSheet.delete();
      await context.sync();
    } catch {
      // Sheet may not exist — treat as changed
    }

    // Create new data sheet with filters + fingerprint
    await createDataSheet(context, data, appliedSummary, timestamp);
    const refreshedSheet = context.workbook.worksheets.getItem(`${SHEET_PREFIXES.MW_RAW_DATA} ${timestamp}`);
    refreshedSheet.customProperties.add(CUSTOM_PROPERTIES.MW_FILTERS, JSON.stringify(filters));
    refreshedSheet.customProperties.add(MW_DATA_FINGERPRINT_PROP, newFingerprint);

    // Check if analysis sheets exist for this timestamp
    const recName = `MW Recommendations ${timestamp}`;
    const recSheet = context.workbook.worksheets.getItemOrNullObject(recName);
    recSheet.load("isNullObject"); // eslint-disable-line office-addins/no-navigational-load

    // Single-recommendations-view flag: under flag-on, the JESS analysis sheet
    // is renamed to "MW Recs Hidden <ts>". Probe that name as a fallback so a
    // flattened-grid creation failure (which would leave the canonical
    // "MW Recommendations <ts>" name unclaimed) doesn't cause analysisExists
    // to falsely return false. Loaded in the same sync batch — no extra round trip.
    const hiddenRecSheet = MW_SINGLE_RECOMMENDATIONS_VIEW
      ? context.workbook.worksheets.getItemOrNullObject(`MW Recs Hidden ${timestamp}`)
      : null;
    if (hiddenRecSheet) {
      hiddenRecSheet.load("isNullObject");
    }

    refreshedSheet.activate();
    await context.sync();

    analysisExists =
      !recSheet.isNullObject || (hiddenRecSheet ? !hiddenRecSheet.isNullObject : false);
  });

  return { timestamp, dataChanged, analysisExists };
}

/**
 * Delete Market Watch sheets.
 * - If `timestamp` is provided, only sheets whose name ends with that timestamp are deleted.
 * - If omitted, ALL sheets prefixed "MW " are deleted.
 * Also cleans up orphaned hidden `_MMData_` sheets.
 */
export async function deleteMarketWatchSheets(
  context: Excel.RequestContext,
  timestamp?: string
): Promise<void> {
  const worksheets = context.workbook.worksheets;
  worksheets.load("items/name,items/visibility");
  await context.sync();

  for (const sheet of worksheets.items) {
    const name = sheet.name;
    if (name.startsWith(SHEET_PREFIXES.MW)) {
      if (!timestamp || name.includes(timestamp)) {
        sheet.delete();
      }
    } else if (name.startsWith("_MMData_")) {
      if (!timestamp || name.includes(timestamp)) {
        sheet.delete();
      }
    }
  }
  await context.sync();
}

// ─── Data reader ────────────────────────────────────────────────

/**
 * Read headers + data rows from an existing MW Raw Data sheet.
 * MW Raw Data layout: rows 1-4 are metadata/filters, row 5 (index 4) is headers,
 * row 6+ (index 5+) is data.  Follows the same layout as readPreservedColumns.
 */
export async function readMWDataFromSheet(
  timestamp: string
): Promise<{ headers: string[]; rows: any[][] }> {
  let headers: string[] = [];
  let rows: any[][] = [];

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(`${SHEET_PREFIXES.MW_RAW_DATA} ${timestamp}`);
    const usedRange = sheet.getUsedRange();
    usedRange.load(["rowCount", "columnCount"]);
    await context.sync();

    if (usedRange.rowCount < 6) {
      throw new Error("MW Raw Data sheet has no header row");
    }

    // Row 6 (0-indexed 5) = headers
    const headerRange = sheet.getRangeByIndexes(5, 0, 1, usedRange.columnCount);
    headerRange.load("values");
    await context.sync();

    headers = (headerRange.values[0] ?? []).map((h: any) => (h != null ? String(h) : ""));

    // Row 7+ (0-indexed 6+) = data
    const dataRowCount = usedRange.rowCount - 6;
    if (dataRowCount > 0) {
      const dataRange = sheet.getRangeByIndexes(6, 0, dataRowCount, usedRange.columnCount);
      dataRange.load("values");
      await context.sync();
      rows = dataRange.values;
    }
  });

  return { headers, rows };
}

// ─── Shared analysis helper ─────────────────────────────────────

/**
 * Send data to the analysis backend, poll until complete, then create
 * Excel sheets (Pivot, Recommendations, Rec Details — and optionally Data).
 *
 * Used by both `executeMarketWatchPipeline` (full run) and
 * `rerunMarketWatchAnalysis` (analysis-only re-run).
 */
async function analyzeAndCreateSheets(
  data: { headers: string[]; rows: any[][] },
  filters: MarketWatchFilterState,
  appliedSummary: string,
  timestamp: string,
  skipDataSheet: boolean,
  onProgress?: (progress: PipelineProgress) => void,
  abortSignal?: PipelineAbortSignal,
  reportWeek?: string
): Promise<void> {
  const { asin, keyword, brand } = filters;

  const report = (percent: number, message: string, phase: string) => {
    if (onProgress) onProgress({ percent, message, phase });
  };

  report(10, "Starting analysis...", "analyze");

  // Build analysis request — exclusion is applied at data fetch; flags are forwarded for analyse context
  const useDynamic = filters.priceClusterMode === PRICE_CLUSTER_MODES.DYNAMIC;
  const analysisRequest: Parameters<typeof startMarketWatchAnalysis>[0] = {
    table_data: { headers: data.headers, rows: data.rows },
    use_dynamic_price_cluster: useDynamic,
    is_enabled_seasonality: filters.isEnabledSeasonality ?? false,
    is_exclude_same_brand: filters.isExcludeSameBrand ?? false,
    exclude_specific_brands: filters.excludedBrands ?? [],
    ...(reportWeek ? { report_week: reportWeek } : {}),
    ...(useDynamic && filters.priceVariancePct != null && {
      price_variance_pct: filters.priceVariancePct,
      variance_growth_factor: filters.varianceGrowthFactor ?? undefined,
      has_cluster_overrides: true
    }),
  };

  // Start analysis job
  console.log("[ClusterSettings] API request body:", {
    use_dynamic_price_cluster: analysisRequest.use_dynamic_price_cluster,
    price_variance_pct: analysisRequest.price_variance_pct,
    variance_growth_factor: analysisRequest.variance_growth_factor,
    has_cluster_overrides: analysisRequest.has_cluster_overrides,
  });
  const { job_id } = await startMarketWatchAnalysis(analysisRequest);

  report(12, "Analysis in progress...", "analyzing");

  // Stream progress via SSE
  const token = await getIdToken();
  const sseUrl = `${config.apiBaseUrl}/market-watch/analyze/progress/${encodeURIComponent(job_id)}?token=${token}`;
  let analysisResult: MarketWatchAnalyzeResult | null = null;

  await new Promise<void>((resolve, reject) => {
    const es = new EventSource(sseUrl);

    es.onmessage = (event) => {
      try {
        if (abortSignal?.cancelled) {
          es.close();
          reject(new Error("Pipeline cancelled"));
          return;
        }

        let rawData: string = event.data;
        if (rawData.startsWith("data: ")) {
          rawData = rawData.slice(6);
        }
        const sanitized = rawData
          .replace(/\bNaN\b/g, "null")
          .replace(/\b-?Infinity\b/g, "null");
        const progressData = JSON.parse(sanitized);
        const backendProgress = progressData.progress || 0;
        const mappedProgress = 10 + Math.floor(backendProgress * 0.85);
        report(mappedProgress, progressData.message || "Analysis in progress...", "analyzing");

        if (progressData.status === "complete" && progressData.result) {
          analysisResult = progressData.result;
          es.close();
          resolve();
        } else if (progressData.status === JOB_STATUS.FAILED) {
          es.close();
          reject(new Error(progressData.error || "Analysis failed"));
        }
      } catch (err) {
        es.close();
        reject(err);
      }
    };

    es.onerror = () => {
      es.close();
      reject(new Error("Lost connection to analysis progress stream. Please try again."));
    };
  });

  if (!analysisResult) {
    throw new Error("Analysis completed but no result received");
  }

  // Debug logging
  console.log("=== MARKET WATCH BACKEND RESPONSE ===");
  console.log("Has excel_styled_report:", !!analysisResult.excel_styled_report);
  console.log("target_asins_analyzed:", analysisResult.target_asins_analyzed);
  console.log("grouped_grid length:", analysisResult.grouped_grid?.length);
  console.log("execution_time_ms:", analysisResult.execution_time_ms);
  console.log("Has updated_table_data:", !!analysisResult.updated_table_data);
  if (analysisResult.updated_table_data) {
    console.log("updated_table_data headers:", analysisResult.updated_table_data.headers?.length);
    console.log("updated_table_data rows:", analysisResult.updated_table_data.rows?.length);
  }
  console.log("=== END BACKEND RESPONSE ===");

  // If the backend returned valid enriched data, use it for MW Raw Data and Pivot sheets.
  // Do NOT mutate `data` directly — use a separate reference.
  // Validate shape: fall back to original data if headers/rows are empty, missing, or mismatched.
  const utd = analysisResult.updated_table_data;
  const hasValidUpdate =
    utd?.headers?.length > 0 && utd?.rows?.length > 0 && utd.rows[0]?.length === utd.headers.length;
  const dataSheetSource = hasValidUpdate ? { headers: utd!.headers, rows: utd!.rows } : data;

  if (utd && !hasValidUpdate) {
    console.warn("updated_table_data validation failed, using original data", {
      hasHeaders: !!utd.headers?.length,
      hasRows: !!utd.rows?.length,
      headerCount: utd.headers?.length,
      firstRowCols: utd.rows?.[0]?.length,
    });
  }

  report(95, "Creating Excel sheets...", "sheets");

  // Extract date range from analysis result
  const datePeriod = {
    startDate: analysisResult!.report_start_date,
    endDate: analysisResult!.report_end_date,
  };

  // Create sheets — order determines left-to-right tab position in Excel.
  // Timing instrumentation logs each phase's wall-time to the browser console
  // under the prefix "[MW Timing]". Filter on that prefix in Edge DevTools
  // (F12 → Console) after running Market Watch to see the breakdown.
  const tExcelRun = performance.now();
  const timings: Record<string, number> = {};

  await Excel.run(async (context) => {
    // Pivots — two tabs in left-to-right order, BOTH reading from a SINGLE
    // shared hidden source sheet (eliminates duplicate dataset write):
    //   1st: "MW Pivot By Price <ts>"   — canonical row order (price above feature)
    //   2nd: "MW Pivot By Feature <ts>" — feature above price; same value fields
    const pivotConfig = analysisResult!.pivot_config;
    const groupedGrid = analysisResult!.grouped_grid;
    const enrichedTable = { headers: dataSheetSource.headers, rows: dataSheetSource.rows };

    if (pivotConfig && enrichedTable.headers?.length > 0 && enrichedTable.rows?.length > 0) {
      // Shared source sheet — created ONCE, consumed by both pivots
      const tSrc = performance.now();
      let sharedSourceTable: Excel.Table | null = null;
      try {
        sharedSourceTable = await createPivotSourceSheet(context, enrichedTable);
      } catch (sourceError) {
        console.error("Failed to create shared pivot source sheet:", sourceError);
      }
      timings["pivot source sheet (shared)"] = performance.now() - tSrc;

      if (sharedSourceTable) {
        // Pivot 1: By Price (canonical row order from pivotConfig)
        const tP1 = performance.now();
        try {
          await createPivotSheet(
            context,
            enrichedTable,
            pivotConfig,
            timestamp,
            `MW Pivot By Price ${timestamp}`,
            sharedSourceTable
          );
        } catch (pivotError) {
          console.error("Failed to create MW Pivot By Price sheet:", pivotError);
        }
        timings["MW Pivot By Price"] = performance.now() - tP1;

        // Pivot 2: By Feature (feature label placed above price label)
        const tP2 = performance.now();
        try {
          const featureFirstRows = buildFeatureFirstRowOrder(pivotConfig.rows ?? []);
          await createPivotSheet(
            context,
            enrichedTable,
            pivotConfig,
            timestamp,
            `MW Pivot By Feature ${timestamp}`,
            sharedSourceTable,
            featureFirstRows
          );
        } catch (pivotError) {
          console.error("Failed to create MW Pivot By Feature sheet:", pivotError);
        }
        timings["MW Pivot By Feature"] = performance.now() - tP2;
      }
    }

    // Analysis report — MW Recommendations (2nd tab)
    const tAnalysis = performance.now();
    let analysisSheet: Excel.Worksheet | null = null;
    if (analysisResult!.excel_styled_report) {
      const jessReport = sanitizeErrorReport(
        analysisResult!.excel_styled_report as ExcelStyledReport
      );
      analysisSheet = await renderExcelStyledReport(
        context,
        jessReport,
        `MW Recommendations ${timestamp}`,
        appliedSummary,
        "Market Watch Analysis Report"
      );
    } else {
      const recommendationText = (analysisResult!.per_asin_analysis || [])
        .map(
          (a) =>
            `=== ${a.target_asin} (${a.analysis_mode}) ===\n` +
            (a.recommendations || [])
              .map((r) => `[Priority ${r.priority}] ${r.action}\n  Rationale: ${r.rationale}`)
              .join("\n\n")
        )
        .join("\n\n");

      const filterContext: FilterContext = {
        asins: asin,
        keywords: keyword,
        isFiltered: asin.length > 0 || keyword.length > 0,
        filterType:
          asin.length > 0 && keyword.length > 0
            ? "both"
            : asin.length > 0
              ? "asin"
              : keyword.length > 0
                ? "keyword"
                : "none",
      };
      analysisSheet = await createAnalysisSheet(
        context,
        "",
        recommendationText || "No recommendations generated.",
        groupedGrid,
        appliedSummary,
        filterContext,
        timestamp,
        datePeriod
      );
    }
    timings["MW Recommendations (analysis report)"] = performance.now() - tAnalysis;

    // Single-recommendations-view flag: hide the JESS analysis sheet and
    // rename it so the canonical "MW Recommendations <ts>" name frees up
    // for the flattened-grid sheet below. Keeps the "MW " prefix so existing
    // tagging/cleanup/route detection continues to work. Toggle the flag
    // off in featureFlags.ts to restore the legacy two-tab behavior.
    if (MW_SINGLE_RECOMMENDATIONS_VIEW && analysisSheet) {
      analysisSheet.name = `MW Recs Hidden ${timestamp}`;
      analysisSheet.visibility = Excel.SheetVisibility.hidden;
      await context.sync();
    }

    // Flattened Grid — MW Rec Details (3rd tab)
    const tFlat = performance.now();
    let flattenedGridCreated = false;
    if (groupedGrid && groupedGrid.length > 0) {
      try {
        await createFlattenedGridSheet(
          context,
          groupedGrid,
          pivotConfig,
          timestamp,
          analysisResult!.per_asin_analysis
        );
        flattenedGridCreated = true;
      } catch (flattenedError) {
        console.error("Failed to create flattened grid sheet:", flattenedError);
      }
    }
    timings["MW Rec Details (flattened grid)"] = performance.now() - tFlat;

    // Single-recommendations-view flag: rename the flattened-grid sheet so
    // its tab label reads "MW Recommendations <ts>". The original
    // "MW Recommendations <ts>" sheet was renamed/hidden above, so this
    // name is free. Skip if creation failed.
    let renamedFlattenedSheet: Excel.Worksheet | null = null;
    if (MW_SINGLE_RECOMMENDATIONS_VIEW && flattenedGridCreated) {
      const flatSheet = context.workbook.worksheets.getItem(`MW Rec Details ${timestamp}`);
      flatSheet.name = `MW Recommendations ${timestamp}`;
      await context.sync();
      renamedFlattenedSheet = flatSheet;
    }

    // Raw Data sheet (4th tab — rightmost)
    const tRaw = performance.now();
    if (!skipDataSheet) {
      await createDataSheet(
        context,
        dataSheetSource,
        appliedSummary,
        timestamp,
        undefined,
        datePeriod
      );

      const fingerprint = await computeDataFingerprint(
        dataSheetSource.headers,
        dataSheetSource.rows
      );
      const dataSheet = context.workbook.worksheets.getItem(`${SHEET_PREFIXES.MW_RAW_DATA} ${timestamp}`);
      dataSheet.customProperties.add(MW_DATA_FINGERPRINT_PROP, fingerprint);
      await context.sync();
    }
    timings["MW Raw Data"] = performance.now() - tRaw;

    // Activate the user-facing primary tab after ALL sheets are created.
    // Flag-on: the renamed flattened-grid sheet is now the visible
    // "MW Recommendations <ts>". Flag-off: the JESS analysisSheet is the
    // visible "MW Recommendations <ts>". Fall back to analysisSheet if the
    // flattened-grid creation failed under flag-on.
    const primaryTabSheet =
      MW_SINGLE_RECOMMENDATIONS_VIEW && renamedFlattenedSheet
        ? renamedFlattenedSheet
        : analysisSheet;
    if (primaryTabSheet) {
      primaryTabSheet.activate();
    }

    // Tag all MW sheets from this run with the applied filter state
    await tagMWRunSheets(context, timestamp, filters);

    await context.sync();
  });

  // Emit timing summary for the sheet-creation phase. Filter on "[MW Timing]"
  // in Edge DevTools console to compare runs after the dataset-write fix.
  const totalMs = performance.now() - tExcelRun;
  const rows = Object.entries(timings)
    .map(([k, v]) => `  ${k.padEnd(38, " ")} ${v.toFixed(0).padStart(6, " ")}ms`)
    .join("\n");
  console.log(
    `[MW Timing] Sheet-creation phase (95→100%): ${totalMs.toFixed(0)}ms\n${rows}\n  ${"-".repeat(44)}\n  ${"TOTAL Excel.run wall-time".padEnd(38, " ")} ${totalMs.toFixed(0).padStart(6, " ")}ms`
  );

  // Persist applied filters to global storage
  await saveMarketWatchFilters(MARKET_WATCH_SHEET, filters);
}

// ─── Main pipeline ───────────────────────────────────────────────

/**
 * Execute the full Market Watch pipeline:
 *   1. Fetch filtered data from backend
 *   2. Start analysis job
 *   3. Poll until complete
 *   4. Create 4 Excel sheets (Data, Pivot, Recommendations, Rec Details)
 *   5. Tag sheets with filter state & persist filters
 *
 * @returns The timestamp string shared by all created sheets.
 */
export async function executeMarketWatchPipeline(
  filters: MarketWatchFilterState,
  onProgress?: (progress: PipelineProgress) => void,
  abortSignal?: PipelineAbortSignal
): Promise<string> {
  const { asin, keyword, brand } = filters;

  const report = (percent: number, message: string, phase: string) => {
    if (onProgress) onProgress({ percent, message, phase });
  };

  // 1. Fetch filtered data (exclusion applied at SQL level)
  report(0, "Fetching data...", "data");
  const data = await fetchMarketWatchData(asin, keyword, brand, {
    isExcludeSameBrand: filters.isExcludeSameBrand,
    excludedBrands: filters.excludedBrands,
    isExcludeSpreetailAsin: filters.isExcludeSpreetailAsin,
  });

  // Build applied filter summary
  const appliedSummary = buildMWFilterSummary(filters);

  // 2–5. Analyze + create sheets (including Data sheet)
  const timestamp = generateTimestamp();
  await analyzeAndCreateSheets(
    data,
    filters,
    appliedSummary,
    timestamp,
    false,
    onProgress,
    abortSignal,
    data.report_week
  );

  return timestamp;
}

// ─── Analysis-only re-run ───────────────────────────────────────

/**
 * Re-run analysis on data already present in an MW Raw Data sheet.
 * Renames the existing Data sheet to a new timestamp and creates fresh
 * Pivot, Recommendations, and Rec Details sheets. Old analysis sheets
 * are preserved for comparison.
 *
 * @returns The new timestamp string used for the fresh sheets.
 */
export async function rerunMarketWatchAnalysis(
  oldTimestamp: string,
  filters: MarketWatchFilterState,
  onProgress?: (progress: PipelineProgress) => void,
  abortSignal?: PipelineAbortSignal
): Promise<string> {
  const report = (percent: number, message: string, phase: string) => {
    if (onProgress) onProgress({ percent, message, phase });
  };

  // 1. Read data from the existing MW Raw Data sheet
  report(0, "Reading existing data...", "data");
  const data = await readMWDataFromSheet(oldTimestamp);

  // 2. Generate new timestamp for the fresh set
  const newTimestamp = generateTimestamp();

  // 3. Rename MW Raw Data sheet to new timestamp & update custom properties
  await Excel.run(async (context) => {
    const dataSheet = context.workbook.worksheets.getItem(`${SHEET_PREFIXES.MW_RAW_DATA} ${oldTimestamp}`);
    dataSheet.name = `${SHEET_PREFIXES.MW_RAW_DATA} ${newTimestamp}`;

    // Update stored filters on the renamed sheet
    dataSheet.customProperties.add(CUSTOM_PROPERTIES.MW_FILTERS, JSON.stringify(filters));

    // Re-stamp the fingerprint (data unchanged, but prop stays associated)
    const fpProp = dataSheet.customProperties.getItemOrNullObject(MW_DATA_FINGERPRINT_PROP);
    fpProp.load("isNullObject,value"); // eslint-disable-line office-addins/no-navigational-load
    await context.sync();

    if (!fpProp.isNullObject) {
      dataSheet.customProperties.add(MW_DATA_FINGERPRINT_PROP, fpProp.value);
    }

    await context.sync();
  });

  // 4. Build filter summary and run analysis (skip Data sheet creation)
  const appliedSummary = buildMWFilterSummary(filters);
  await analyzeAndCreateSheets(
    data,
    filters,
    appliedSummary,
    newTimestamp,
    true,
    onProgress,
    abortSignal
  );

  return newTimestamp;
}
